/**
 * Session Cleanup Service
 *
 * Centralised sessionStorage wipe for logout / session expiration.
 *
 * Why a whitelist (and not a per-key removal list):
 * sessionStorage in this app holds session-scoped UI state that is
 * almost always either user-bound (guest scheduling drafts, the card
 * preview modal payload) or per-admin-context (the AI models banner
 * cache, MSAL interaction state). When the user signs out, any of
 * those leaking into the next account's session has caused real bugs
 * — the most recent being a guest flower order draft from a fresh
 * sign-up replaying against the user's existing account after a
 * logout + log-in on the same tab.
 *
 * A per-key removal list is fragile: every time a feature adds a new
 * sessionStorage key, the developer has to remember to also wire up
 * the cleanup, and the only way to find the gap is for it to surface
 * as a production bug. Inverting the list — wipe everything by
 * default, opt out for the rare survivor — makes the safe behavior
 * the default.
 *
 * When to add a key to {@link SESSION_STORAGE_LOGOUT_WHITELIST}:
 *   1. The key contains NO user-bound or account-bound data
 *      (so leaking it across an account switch is harmless), AND
 *   2. The key MUST persist across the logout boundary because
 *      something downstream depends on it being there in the next
 *      signed-in session (very rare).
 *
 * Acquisition attribution (`scheduledsmiles_acquisition_attribution` and
 * `scheduledsmiles_acquisition_attribution_last_touch`) is intentionally NOT
 * whitelisted: it is flushed to the User row after auth (PBI #1104 / #1204).
 * Surviving logout would risk attributing the next account on the same tab.
 *
 * If criterion (1) is uncertain, do NOT whitelist — let the value be
 * cleared. Recomputing it on the next sign-in is almost always
 * cheaper than auditing a privacy leak.
 */

import {
  clearAcquisitionAttribution,
  clearSignupUtmSnapshot,
} from './acquisitionAttributionService';

/** One-shot landing banner after admin account disable (PBI #1230). Not user-bound. */
const ACCOUNT_DISABLED_BANNER_KEY = 'accountDisabledBanner';

/**
 * sessionStorage keys that explicitly survive logout. Currently empty:
 * every consumer in the app today either holds user-bound state, an
 * admin-only cache, or transient in-progress auth state, none of
 * which should leak into the next sign-in on the same tab. Read the
 * top-of-file comment for the criteria before adding to this list.
 */
export const SESSION_STORAGE_LOGOUT_WHITELIST: readonly string[] = [
  ACCOUNT_DISABLED_BANNER_KEY,
];

/** sessionStorage key for the one-shot account-disabled landing banner. */
export { ACCOUNT_DISABLED_BANNER_KEY };

/**
 * Removes every sessionStorage entry except those in
 * {@link SESSION_STORAGE_LOGOUT_WHITELIST}. Best-effort: in private
 * browsing modes where sessionStorage throws, this is a no-op (same
 * defensive pattern as {@link guestDraftService.clearGuestFlowerDraft}).
 *
 * Called by {@link AuthContext.logout} on user-initiated sign-out and
 * by {@link AuthContext.handleAuthExpiration} when a refreshed token
 * still returns 401 (the apiClient's "session truly expired" path).
 */
export const clearSessionStorageForLogout = (): void => {
  clearAcquisitionAttribution();
  clearSignupUtmSnapshot();
  try {
    // Snapshot the keys first because removing while iterating
    // sessionStorage shifts the indices and skips entries.
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && !SESSION_STORAGE_LOGOUT_WHITELIST.includes(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable (private browsing / quota) — best-effort.
  }
};
